/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils';
import type { GanttViewportTaskLayoutObj, ViewportLayout } from './utils/dataLayoutUtils';
import { useMemo, useRef } from 'preact/hooks';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { getTaskAriaLabel } from './utils/ariaUtils';
import type { GanttRowData, GanttTaskData } from './gantt.types';

type Props<K1, K2, D1 extends GanttRowData<K1, D1, D2>, D2 extends GanttTaskData<K2>> = {
  width: number;
  height: number;
  rowData: ViewportLayout<K1, K2, D1, D2>['rowData'];
  dateFormatter: (date: string) => string;
  ariaActiveId?: string;
  ariaActiveTaskObj?: GanttViewportTaskLayoutObj<K2>;
};

export function GanttDesktopTaskAccContent<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  width,
  height,
  rowData,
  dateFormatter,
  ariaActiveId,
  ariaActiveTaskObj
}: Props<K1, K2, D1, D2>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const idIndex = useRef(0);
  const ids = useMemo(() => {
    const ids =
      idIndex.current === 0 ? [ariaActiveId, getRandomId()] : [getRandomId(), ariaActiveId];
    idIndex.current = Math.abs(idIndex.current - 1);
    return ids;
  }, [ariaActiveId]);

  const ariaLabel =
    ariaActiveTaskObj && getTaskAriaLabel(ariaActiveTaskObj, rowData, dateFormatter, translations);

  return (
    <div style={{ width, height }}>
      <div role="img" id={ids[0]} aria-label={ariaLabel}></div>
      <div role="img" id={ids[1]} aria-label={ariaLabel}></div>
    </div>
  );
}
